import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { billingCreate, billingImport } from '@/modules/billing/public';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { BILLING_LIST_PATH } from './wallet-dashboard.links';
import { PERIOD_PRESETS } from './wallet-dashboard.period';

// Manifesto do Dashboard de Gestão de carteira (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// No legado: `/gestao-carteira/dashboard`, dentro do grupo com `BillingAccount:Read` (router.tsx:1341-1354);
// `/gestao-carteira` redireciona para cá.

export const WALLET_DASHBOARD_PATH = '/gestao-carteira/dashboard';
export const WALLET_DASHBOARD_READ = can('BillingAccount', 'Read');

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra o Dashboard de Gestão de carteira para usar esta ação.`);
};

const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Um botão (`preset`) ou um intervalo (`start` e `end`), nunca os dois. */
export const walletDashboardPeriodInput = z
  .strictObject({
    preset: z.enum(PERIOD_PRESETS).optional(),
    start: civilDate.optional(),
    end: civilDate.optional(),
  })
  .refine((value) => (value.preset === undefined ? value.start !== undefined && value.end !== undefined : value.start === undefined && value.end === undefined), {
    message: 'use preset, or start and end',
  })
  .refine((value) => value.start === undefined || value.end === undefined || value.start <= value.end, {
    message: 'start must not be after end',
  });
export type WalletDashboardPeriodInput = z.infer<typeof walletDashboardPeriodInput>;

// Descrições para o agente em pt-BR com `msg`, servidas no idioma do usuário (U46). Na ordem dos botões do legado
// (BillingActions.tsx:24-51): Nova cobrança, Importar arquivo CNAB, Selecionar carteiras; depois o período.
// Nova cobrança e importação são as mesmas operações de Cobranças: tipo `write`, entrada vazia e prévia vêm de
// `billing` (billing.actions.ts:17-43); muda só o id, o rótulo do legado e o `run`, que a tela liga.
export const walletDashboardNewCharge = action({
  ...billingCreate,
  id: 'walletDashboard.newCharge',
  keywords: [msg`cobrança`, msg`boleto`],
  run: mountedScreenRequired,
});

export const walletDashboardImportCnab = action({
  ...billingImport,
  id: 'walletDashboard.importCnab',
  label: msg`Importar arquivo CNAB`,
  keywords: [msg`cnab`, msg`remessa`],
  run: mountedScreenRequired,
});

export const walletDashboardWallets = action({
  id: 'walletDashboard.wallets',
  kind: 'draft',
  label: msg`Selecionar carteiras`,
  description: msg`Abre a gaveta "Gerenciar Carteiras" para a pessoa escolher quais carteiras o Dashboard considera. A escolha e a confirmação são da pessoa.`,
  keywords: [msg`carteiras`, msg`convênio`],
  run: mountedScreenRequired,
});

export const walletDashboardPeriod = action<WalletDashboardPeriodInput>({
  id: 'walletDashboard.period',
  kind: 'read',
  label: msg`Escolher período`,
  description: msg`Troca o período dos valores Registrados, Pagos e Total em aberto. Use um botão (today = Hoje, yesterday = Ontem, 1w = 1 semana, 1m, 3m ou 6m) ou um intervalo de datas (AAAA-MM-DD). As carteiras escolhidas continuam.`,
  keywords: [msg`hoje`, msg`ontem`, msg`período`],
  input: walletDashboardPeriodInput,
  run: mountedScreenRequired,
});

/** A busca da lista de Cobranças que os números do painel abrem (os mesmos parâmetros do legado). */
export const walletDashboardOpenBillingsInput = z.strictObject({
  initialDueDate: civilDate.optional(),
  finalDueDate: civilDate.optional(),
  initialCreatedDate: civilDate.optional(),
  finalCreatedDate: civilDate.optional(),
  statusesValue: z.array(z.string().min(1)).optional(),
  walletsCode: z.array(z.string().regex(/^\d+$/)).optional(),
});
export type WalletDashboardOpenBillingsInput = z.infer<typeof walletDashboardOpenBillingsInput>;

export const walletDashboardOpenBillings = action<WalletDashboardOpenBillingsInput>({
  id: 'walletDashboard.openBillings',
  kind: 'navigate',
  label: msg`Ver cobranças`,
  description: msg`Abre, na mesma aba, a lista de Cobranças filtrada como o número clicado do Dashboard: vencimento ou criação num intervalo (AAAA-MM-DD), status e carteiras. Não muda nada.`,
  input: walletDashboardOpenBillingsInput,
  run: ({ input, nav }) => nav.to(BILLING_LIST_PATH, { search: { ...input } }),
});

export const walletDashboardScreen = defineScreen({
  id: 'walletDashboard.home',
  path: WALLET_DASHBOARD_PATH,
  title: msg`Dashboard`,
  description: msg`Painel das cobranças da carteira: valores registrados, pagos e em aberto no período, e o total e o volume por faixa de dias das cobranças em aberto e das vencidas, nas carteiras escolhidas.`,
  permission: WALLET_DASHBOARD_READ,
  place: { icon: 'layout-dashboard', section: 'wallet-management' },
  actions: [walletDashboardNewCharge, walletDashboardImportCnab, walletDashboardWallets, walletDashboardPeriod, walletDashboardOpenBillings],
});

/** O que o agente lê da tela (U24), no idioma do usuário (U46): período, carteiras e as contagens que aparecem. */
export function walletDashboardContext(
  input: {
    readonly period: string;
    readonly wallets: readonly string[];
    readonly open: number | null | undefined;
    readonly overdue: number | null | undefined;
    readonly hidden: boolean;
    readonly loading: boolean;
  },
  i18n: I18n,
): ScreenContext {
  const period = input.period;
  const count = input.wallets.length;
  const scope = count > 0 ? i18n._(msg`Carteiras escolhidas: ${count}.`) : i18n._(msg`Todas as carteiras.`);
  const open = input.open ?? 0;
  const overdue = input.overdue ?? 0;
  const numbers = input.loading ? i18n._(msg`Carregando.`) : i18n._(msg`Cobranças em aberto: ${open}. Cobranças vencidas: ${overdue}.`);
  const hidden = input.hidden ? ` ${i18n._(msg`Os valores estão ocultos na tela.`)}` : '';
  return {
    summary: `${i18n._(msg`Dashboard de Gestão de carteira.`)} ${i18n._(msg`Período: ${period}.`)} ${scope} ${numbers}${hidden}`,
    fields: {
      wallets: count,
      open: input.open ?? null,
      overdue: input.overdue ?? null,
      hidden: input.hidden,
    },
    sensitivity: 'internal',
  };
}
